import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Package, PlusCircle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import { useToast } from '@/components/ui/use-toast';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';

const ITEMS_PER_PAGE = 10;

const SolicitudInsumosListPage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [displayedData, setDisplayedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const columns = [
    {
      id: 'createdAt',
      label: 'Fecha',
      sortable: true,
      render: (row) => new Date(row.createdAt).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    {
      id: 'creator',
      label: 'Solicitante',
      sortable: true,
      render: (row) => row.creator?.nombre || '-'
    },
    {
      id: 'edificio',
      label: 'Portafolio',
      sortable: true,
      render: (row) => (
        <div>
          <p className="font-medium">{row.edificio?.nombre}</p>
          <p className="text-xs text-gray-500">{row.edificio?.ciudad}</p>
        </div>
      )
    },
    {
      id: 'total_solicitado',
      label: 'Total Solicitado',
      sortable: true,
      render: (row) => (
        <Badge variant="outline">{row.total_solicitado} unidades</Badge>
      )
    },
    {
      id: 'total_entregado',
      label: 'Total Entregado',
      sortable: true,
      render: (row) => (
        <Badge variant={row.total_entregado === row.total_solicitado ? 'default' : 'secondary'}>
          {row.total_entregado} unidades
        </Badge>
      )
    },
    {
      id: 'estado',
      label: 'Estado',
      sortable: true,
      render: (row) => {
        const colors = {
          'Pendiente': 'bg-yellow-100 text-yellow-800',
          'En Proceso': 'bg-blue-100 text-blue-800',
          'Completada': 'bg-green-100 text-green-800',
          'Cancelada': 'bg-red-100 text-red-800'
        };
        return (
          <Badge className={colors[row.estado] || 'bg-gray-100 text-gray-800'}>
            {row.estado}
          </Badge>
        );
      }
    },
    {
      id: 'items',
      label: 'Items',
      render: (row) => (
        <span className="text-sm text-gray-600">{row.items?.length || 0} tipo(s)</span>
      )
    }
  ];

  const fetchSolicitudes = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/solicitud-insumos/filter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          limit: ITEMS_PER_PAGE,
          offset: currentPage,
          query: {}
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setDisplayedData(data.data || []);
        setTotalItems(data.totalRecords || 0);
      } else {
        throw new Error(data.message || 'Error al cargar solicitudes');
      }
    } catch (error) {
      console.error('Error al cargar solicitudes:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar las solicitudes",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, toast]);

  useEffect(() => {
    fetchSolicitudes();
  }, [fetchSolicitudes]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleNuevaSolicitud = () => {
    navigate('/propiedades');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      <Card className="card-gradient-bg shadow-lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-purple-100 rounded-full">
                <Package className="h-8 w-8 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold text-foreground">
                  Solicitudes de Insumos
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  Gestiona todas las solicitudes de insumos operativos
                </CardDescription>
              </div>
            </div>
            <Button onClick={handleNuevaSolicitud}>
              <PlusCircle className="h-4 w-4 mr-2" />
              Nueva Solicitud
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <DataTable
            columns={columns}
            data={displayedData}
            isLoading={isLoading}
          />
          
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(totalItems / ITEMS_PER_PAGE)}
            onPageChange={handlePageChange}
          />
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default SolicitudInsumosListPage;
